import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Download, Search } from 'lucide-react'
import { csvFileName, rowsToCsv } from '@/lib/shiftExport'
import type { ShiftCell, ShiftRow, ShiftTileDetail } from '@/api/shiftDashboardApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PlannerPagination } from '@/components/equipment/PlannerPagination'
import { ALL_FACET, cellText, pageSlice, rowsInFacet, searchRows } from '@/lib/shiftTiles'
import { cn } from '@/lib/utils'
import { StatusChip } from './ShiftTiles'

const TONE: Record<string, string> = {
  critical: 'font-semibold text-red-700',
  attention: 'font-medium text-amber-700',
  ready: 'text-green-700',
  info: 'text-blue-700',
  muted: 'text-muted-foreground',
}

/**
 * One tile opened (z8nrz7fbcr): quick-filter chips with their counts, a table
 * of the very rows the tile counted, and the selected row's detail on the
 * right. Read-only: links open the record, nothing here changes it.
 */
export function ShiftDrilldown({
  detail,
  loading,
  error,
  scope,
  date,
}: {
  detail: ShiftTileDetail | undefined
  loading: boolean
  error: Error | null
  /** What the rows are scoped to, one part per chip: site, room, day, shift, team. */
  scope: string[]
  /** The shift's day, YYYY-MM-DD, for the export's file name. */
  date: string
}) {
  const [facet, setFacet] = useState(ALL_FACET)
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // A different tile starts clean.
  useEffect(() => {
    setFacet(ALL_FACET)
    setQuery('')
    setSelectedId(null)
    setPage(1)
  }, [detail?.id])

  // A new chip or search starts at the first page.
  useEffect(() => setPage(1), [facet, query])

  const rows = useMemo(
    () => (detail ? searchRows(rowsInFacet(detail.rows, facet), query) : []),
    [detail, facet, query],
  )
  // Clamped: a background refresh that shortens the list never strands the reader on an empty page.
  const paged = pageSlice(rows, page, pageSize)
  // Looked up in every row, not the filtered ones: the open record stays open
  // if a background refresh or a search would otherwise hide it.
  const selected = (selectedId && detail?.rows.find((r) => r.id === selectedId)) || null

  if (error) {
    return (
      <p role='alert' className='rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800'>
        {error.message}
      </p>
    )
  }
  if (loading && !detail) return <div className='h-72 animate-pulse rounded-xl bg-muted motion-reduce:animate-none' />
  if (!detail) return null

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
        <h2 className='text-lg font-semibold text-foreground'>{detail.title}</h2>
        <ul aria-label='Scope' className='flex flex-wrap gap-1.5'>
          {scope.map((part) => (
            <li key={part} className='rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground'>
              {part}
            </li>
          ))}
        </ul>
      </div>
      {detail.note && <p className='text-sm text-muted-foreground'>{detail.note}</p>}

      <div className='flex flex-wrap items-center gap-2'>
        <div className='relative'>
          <Search aria-hidden className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search'
            aria-label={`Search ${detail.title}`}
            className='h-9 w-56 pl-8'
          />
        </div>
        <div role='group' aria-label='Quick filters' className='flex flex-wrap gap-1.5'>
          <FacetChip label='All' count={detail.rows.length} active={facet === ALL_FACET} onClick={() => setFacet(ALL_FACET)} />
          {detail.facets.map((f) => (
            <FacetChip key={f.key} label={f.label} count={f.count} active={facet === f.key} onClick={() => setFacet(f.key)} />
          ))}
        </div>
        <Button
          size='sm'
          variant='outline'
          className='ml-auto bg-white'
          disabled={rows.length === 0}
          onClick={() => downloadCsv(rowsToCsv(detail.columns, rows), csvFileName(detail.id, date))}
          title='Download the rows shown, with the current search and quick filter'
        >
          <Download aria-hidden className='mr-1.5 h-3.5 w-3.5' /> Export CSV
        </Button>
      </div>

      <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
        <div>
          {/* The app's shared table (components/ui/table): same header, scrollbar and row states as every other list. */}
          <Table>
            <TableHeader>
              <TableRow>
                {detail.columns.map((c) => (
                  <TableHead key={c.key} scope='col' className={cn('whitespace-nowrap', c.align === 'right' && 'text-right')}>
                    {c.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow className='hover:bg-transparent'>
                  <TableCell colSpan={detail.columns.length} className='py-12 text-center text-sm text-muted-foreground'>
                    {detail.rows.length === 0
                      ? 'Nothing here for this shift.'
                      : 'No rows match. Try another quick filter or clear the search.'}
                  </TableCell>
                </TableRow>
              ) : (
                paged.rows.map((row) => {
                  const isSelected = selected?.id === row.id
                  return (
                    <TableRow
                      key={row.id}
                      onClick={() => setSelectedId(row.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedId(row.id)
                        }
                      }}
                      tabIndex={0}
                      aria-label={`${row.detail.title}. Show details`}
                      data-state={isSelected ? 'selected' : undefined}
                      className='cursor-pointer focus-visible:bg-muted/60 focus-visible:outline-none'
                    >
                      {detail.columns.map((c, i) => (
                        <TableCell
                          key={c.key}
                          className={cn(
                            'align-top',
                            c.align === 'right' && 'text-right tabular-nums',
                            i === 0 && 'font-medium text-foreground',
                          )}
                        >
                          <Cell cell={row.cells[c.key] ?? null} />
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
          {rows.length > 0 && (
            <div className='px-4 pb-3'>
              <PlannerPagination
                page={paged.page}
                pageSize={pageSize}
                total={rows.length}
                summarySuffix={rows.length !== detail.rows.length ? `(filtered from ${detail.rows.length})` : undefined}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
              />
            </div>
          )}
          {detail.truncated && (
            <p className='border-t px-3 py-2 text-xs text-muted-foreground'>
              Showing the first {detail.rows.length}. Narrow the filters to see the rest.
            </p>
          )}
        </div>
      </div>

      <RowDetailDialog row={selected} onClose={() => setSelectedId(null)} />
    </div>
  )
}

/** Hands the viewer a file. The text is built (and made spreadsheet-safe) by rowsToCsv. */
function downloadCsv(csv: string, fileName: string) {
  // A BOM, so Excel reads the UTF-8 (en dashes, accented names) correctly.
  const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

function FacetChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-pressed={active}
      disabled={count === 0 && !active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-40',
        active
          ? 'border-blue-300 bg-blue-50 text-blue-700'
          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50',
      )}
    >
      {label}
      <span
        className={cn(
          'rounded-full px-1.5 tabular-nums',
          active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500',
        )}
      >
        {count}
      </span>
    </button>
  )
}

function Cell({ cell }: { cell: ShiftCell }) {
  if (cell === null || cell === '') return <span className='text-muted-foreground'>—</span>
  if (typeof cell !== 'object') return <>{cell}</>
  const cls = cell.tone ? TONE[cell.tone] : undefined
  if (cell.href) {
    return (
      <Link to={cell.href} onClick={(e) => e.stopPropagation()} className={cn('font-medium text-primary hover:underline', cls)}>
        {cell.text}
      </Link>
    )
  }
  return <span className={cls}>{cellText(cell)}</span>
}

/**
 * A row's detail, in a modal over the table: the table keeps its full width,
 * and Escape or the close button return to the same place in the list.
 * Read-only; "Open record" leaves for the record's own page.
 */
function RowDetailDialog({ row, onClose }: { row: ShiftRow | null; onClose: () => void }) {
  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      {row && (
        <DialogContent className='gap-0 p-0 sm:max-w-lg'>
          <DialogHeader className='space-y-2 border-b px-6 py-5 text-left'>
            <DialogTitle className='text-lg'>{row.detail.title}</DialogTitle>
            {row.detail.subtitle ? (
              <DialogDescription>{row.detail.subtitle}</DialogDescription>
            ) : (
              <DialogDescription className='sr-only'>Details for {row.detail.title}</DialogDescription>
            )}
            <div className='flex flex-wrap gap-1.5'>
              <StatusChip status={row.status} />
              {row.detail.badges?.map((b) => <StatusChip key={b.label} status={b.tone} label={b.label} />)}
            </div>
          </DialogHeader>
          <dl className='max-h-[60vh] space-y-3 overflow-y-auto px-6 py-5 text-sm'>
            {row.detail.fields.map((f) => (
              <div key={f.label} className='grid grid-cols-[9rem_1fr] gap-4'>
                <dt className='text-gray-500'>{f.label}</dt>
                <dd className='font-medium text-gray-900'>{f.value || '—'}</dd>
              </div>
            ))}
          </dl>
          <DialogFooter className='border-t px-6 py-4'>
            <Button variant='ghost' onClick={onClose}>
              Close
            </Button>
            {row.href && (
              <Button asChild>
                <Link to={row.href}>
                  Open record <ArrowUpRight aria-hidden className='ml-1.5 h-4 w-4' />
                </Link>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  )
}
